import {useEffect, useRef, useState} from 'react';
import {Outlet, useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {Alert, AlertDescription} from '@/components/ui/alert.tsx';
import {Badge} from '@/components/ui/badge.tsx';
import {Button} from '@/components/ui/button.tsx';
import {Empty, EmptyDescription, EmptyHeader, EmptyTitle} from '@/components/ui/empty.tsx';
import {LoadingSpinner} from '../../../components/LoadingSpinner';
import {PeriodPicker} from '../../../components/period-picker/period-picker';
import {useBreadcrumbPageTitle} from '../../../routes/breadcrumb-page-title';
import {ProductPageProvider} from './product-context';
import {ProductNav} from './product-nav';
import {readPeriod, samePeriod, writePeriod} from './product-search';
import {useProductPage} from './use-product-page';

export function ProductPage() {
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [initialPeriod] = useState(() => readPeriod(searchParams));
	const state = useProductPage(initialPeriod);
	const echoedPeriod = useRef(initialPeriod);
	const writtenPeriod = useRef(initialPeriod);
	const periodActions = useRef(state.period);
	periodActions.current = state.period;
	const {setTitle} = useBreadcrumbPageTitle();
	const hidePeriod = pathname.endsWith('/manage') || pathname.endsWith('/analytics/runs');

	useEffect(() => {
		setTitle(state.product?.name ?? null);
		return () => setTitle(null);
	}, [state.product?.name, setTitle]);

	useEffect(() => {
		const fromUrl = readPeriod(searchParams);
		const period = periodActions.current;
		if (!period.ready || !fromUrl) return;
		if (echoedPeriod.current && samePeriod(echoedPeriod.current, fromUrl)) return;
		echoedPeriod.current = fromUrl;
		if (fromUrl.toggle === 'week') period.applyWeek();
		else if (fromUrl.toggle === 'month' && fromUrl.month) period.applyMonth(fromUrl.month);
		else if (fromUrl.toggle === 'custom') period.applyCustom(fromUrl.customFrom, fromUrl.customTo);
		else if (fromUrl.toggle === 'presets' && fromUrl.presetId) period.applyPreset(fromUrl.presetId);
	}, [searchParams]);

	useEffect(() => {
		const committed = state.period.committed;
		if (!committed) return;
		if (writtenPeriod.current && samePeriod(writtenPeriod.current, committed)) return;
		writtenPeriod.current = committed;
		echoedPeriod.current = committed;
		setSearchParams((prev) => {
			const current = readPeriod(prev);
			if (current && samePeriod(committed, current)) return prev;
			return writePeriod(prev, committed);
		}, {replace: true});
	}, [setSearchParams, state.period.committed]);

	const stockBadge = stockStatus(state);

	return (
		<div className="mx-auto flex w-full min-w-0 flex-col gap-4 p-4 max-w-6xl">
			<div className="sticky top-0 z-10 flex flex-col gap-3 bg-background py-2">
				<div className="flex items-center gap-3">
					{state.product?.imageUrl ? (
						<img src={state.product.imageUrl} alt="" className="size-14 rounded-2xl bg-muted object-contain"/>
					) : null}
					<div className="min-w-0 flex-1">
						<h1 className="truncate text-xl font-semibold tracking-tight">{state.product?.name ?? 'Товар'}</h1>
						<p className="truncate text-sm text-muted-foreground">
							{state.offerId}
							{state.product?.ozonSku ? ` · SKU ${state.product.ozonSku}` : ''}
						</p>
					</div>
					<Button type="button" variant="ghost" size="sm" onClick={() => navigate('/ozon/products')}>
						Назад
					</Button>
				</div>
				{stockBadge ? <Badge variant="outline" className="w-fit">{stockBadge}</Badge> : null}
				<ProductNav/>
				{!hidePeriod && state.product?.ozonSku ? (
					<PeriodPicker {...state.period} dateFieldIdPrefix="product"/>
				) : null}
			</div>

			{state.error ? (
				<Alert variant="destructive">
					<AlertDescription>{state.error}</AlertDescription>
				</Alert>
			) : null}
			{state.loading ? <LoadingSpinner compact/> : null}
			{state.productNotFound ? (
				<Empty className="border border-border">
					<EmptyHeader>
						<EmptyTitle>Товар не найден</EmptyTitle>
						<EmptyDescription>Проверьте артикул или вернитесь к списку</EmptyDescription>
					</EmptyHeader>
				</Empty>
			) : null}
			{!state.loading && state.product ? (
				<ProductPageProvider value={state}>
					<Outlet/>
				</ProductPageProvider>
			) : null}
		</div>
	);
}

function stockStatus(state: ReturnType<typeof useProductPage>): string | null {
	if (!state.product) return null;
	if (!state.product.ozonSku) return 'Нет SKU Ozon';
	const days = state.salesDynamics?.statistics ?? [];
	const last = [...days].reverse().find((day) => day.inStock != null);
	if (!last || last.inStock == null) return null;
	return last.inStock > 0 ? 'В наличии' : 'Нет на складе';
}
